import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { uploadEntityAttachment } from '@/api/common';
import { deviceDetailsKeys } from '@/features/devices/hooks';
import {
  fetchDeviceContracts,
  fetchContractDocuments,
  createServiceContract,
  patchServiceContract,
  deleteServiceContract,
  contractAttachments,
} from '../api';
import type {
  CreateServiceContractRequest,
  PatchServiceContractRequest,
} from '../types';

export const serviceContractKeys = {
  all: ['serviceContracts'] as const,
  byDevice: (deviceId: string) => [...serviceContractKeys.all, 'device', deviceId] as const,
  documents: (contractId: string) =>
    [...serviceContractKeys.all, 'documents', contractId] as const,
};

// useDeviceContracts loads every contract belonging to a device (admin view).
export function useDeviceContracts(deviceId: string, enabled = true) {
  return useQuery({
    queryKey: serviceContractKeys.byDevice(deviceId),
    queryFn: () => fetchDeviceContracts(deviceId),
    enabled: enabled && Boolean(deviceId),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

// useContractDocuments loads the documents attached to a single contract.
export function useContractDocuments(contractId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: serviceContractKeys.documents(contractId ?? ''),
    queryFn: () => fetchContractDocuments(contractId as string),
    enabled: enabled && Boolean(contractId),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

// useCreateServiceContract creates a new contract for a device and refreshes the views.
export function useCreateServiceContract(deviceId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (request: CreateServiceContractRequest) => createServiceContract(request),
    onSuccess: () => {
      invalidateContractViews(queryClient, deviceId);
      toast.success(t('serviceContracts.toast.createSuccess'));
    },
    onError: () => toast.error(t('serviceContracts.toast.createError')),
  });
}

// usePatchServiceContract applies a lifecycle transition to a contract.
export function usePatchServiceContract(deviceId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      contractId,
      request,
    }: {
      contractId: string;
      request: PatchServiceContractRequest;
    }) => patchServiceContract(contractId, request),
    onSuccess: () => {
      invalidateContractViews(queryClient, deviceId);
      toast.success(t('serviceContracts.toast.updateSuccess'));
    },
    onError: () => toast.error(t('serviceContracts.toast.updateError')),
  });
}

// useDeleteServiceContract permanently deletes a contract and closes its side panel.
export function useDeleteServiceContract(deviceId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (contractId: string) => deleteServiceContract(contractId),
    onSuccess: () => {
      invalidateContractViews(queryClient, deviceId);
      toast.success(t('serviceContracts.toast.deleteContractSuccess'));
    },
    onError: () => toast.error(t('serviceContracts.toast.deleteContractError')),
  });
}

// useUploadContractDocument runs the three-step upload (create record, PUT file, enable).
// Reused by both the create form and the active-contract document section.
export function useUploadContractDocument(deviceId: string, contractId: string | undefined) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({ file, targetContractId }: { file: File; targetContractId?: string }) => {
      const contract = targetContractId ?? contractId;
      if (!contract) throw new Error('Missing contract id for document upload');
      await uploadEntityAttachment(contractAttachments(contract), {
        file,
        fallbackMimeType: 'application/pdf',
      });
    },
    onSuccess: (_data, variables) => {
      const contract = variables.targetContractId ?? contractId;
      if (contract) {
        queryClient.invalidateQueries({ queryKey: serviceContractKeys.documents(contract) });
      }
      invalidateContractViews(queryClient, deviceId);
      toast.success(t('serviceContracts.toast.uploadSuccess'));
    },
    onError: () => toast.error(t('serviceContracts.toast.uploadError')),
  });
}

// useDeleteContractDocument soft-deletes a document from a contract.
export function useDeleteContractDocument(deviceId: string, contractId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (documentId: string) => contractAttachments(contractId).remove(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceContractKeys.documents(contractId) });
      invalidateContractViews(queryClient, deviceId);
      toast.success(t('serviceContracts.toast.deleteSuccess'));
    },
    onError: () => toast.error(t('serviceContracts.toast.deleteError')),
  });
}

// invalidateContractViews refreshes both the contracts list and the device details
// (whose embedded serviceContract summary drives the read-only view).
function invalidateContractViews(
  queryClient: ReturnType<typeof useQueryClient>,
  deviceId: string
) {
  queryClient.invalidateQueries({ queryKey: serviceContractKeys.byDevice(deviceId) });
  queryClient.invalidateQueries({ queryKey: deviceDetailsKeys.detail(deviceId) });
}
